import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Announcement } from '../../models/announcement';
import { AnnouncementReadStatus } from '../../models/announcement-read-status';
import { TableRequestData } from '../../models/table/table-request-param';
import { TableResponse } from '../../models/table-response';
import { HttpUtils } from '../../utils/http.utils';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AnnouncementHistoryEntry } from '../../models/announcement-history-entry';
import { AnnouncementDetails } from '../../models/announcement-details';
import { AnnouncementRecipient } from '../../models/announcement-recipient';
import { AnnouncementCreateRequest } from '../../models/announcement-create-request';
import { AnnouncementEmailStats } from '../../models/announcement-email-stats';

@Injectable({ providedIn: 'root' })
export class AnnouncementsApi {
  #httpClient: HttpClient = inject(HttpClient);
  readonly #apiUrl = environment.API_EWNIOSKI + '/admin/announcements';

  getAnnouncements$(
    tableRequest: TableRequestData,
  ): Observable<TableResponse<Announcement>> {
    return this.#httpClient.get<TableResponse<Announcement>>(
      `${this.#apiUrl}`,
      {
        params: HttpUtils.toHttpParams(tableRequest),
        withCredentials: true,
      },
    );
  }

  getAnnouncementDetails$(id: number): Observable<AnnouncementDetails> {
    return this.#httpClient.get<AnnouncementDetails>(`${this.#apiUrl}/${id}`, {
      withCredentials: true,
    });
  }

  searchAnnouncementRecipients$(
    tableRequest: TableRequestData,
  ): Observable<TableResponse<AnnouncementRecipient>> {
    return this.#httpClient.get<TableResponse<AnnouncementRecipient>>(
      `${this.#apiUrl}/recipients/search`,
      {
        params: HttpUtils.toHttpParams(tableRequest),
        withCredentials: true,
      },
    );
  }

  createAnnouncement$(
    payload: AnnouncementCreateRequest,
  ): Observable<AnnouncementDetails> {
    return this.#httpClient.post<AnnouncementDetails>(
      `${this.#apiUrl}`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  updateAnnouncement$(
    payload: AnnouncementDetails,
  ): Observable<AnnouncementDetails> {
    return this.#httpClient.put<AnnouncementDetails>(
      `${this.#apiUrl}/update`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  public publishAnnouncement$(id: number): Observable<void> {
    return this.#httpClient.put<void>(
      `${this.#apiUrl}/publish/${id}`,
      {},
      { withCredentials: true },
    );
  }

  public archiveAnnouncement$(id: number): Observable<void> {
    return this.#httpClient.put<void>(
      `${this.#apiUrl}/archive/${id}`,
      {},
      { withCredentials: true },
    );
  }

  public deleteAnnouncement$(id: number): Observable<void> {
    return this.#httpClient.delete<void>(`${this.#apiUrl}/${id}`, {
      withCredentials: true,
    });
  }

  getAnnouncementHistoryTable$(
    id: number,
    params: TableRequestData,
  ): Observable<TableResponse<AnnouncementHistoryEntry>> {
    return this.#httpClient.get<TableResponse<AnnouncementHistoryEntry>>(
      `${this.#apiUrl}/${id}/historia`,
      {
        params: HttpUtils.toHttpParams(params),
        withCredentials: true,
      },
    );
  }

  getAnnouncementReadStatus$(id: number): Observable<AnnouncementReadStatus> {
    return this.#httpClient.get<AnnouncementReadStatus>(
      `${this.#apiUrl}/${id}/read-status`,
      { withCredentials: true },
    );
  }

  getAnnouncementEmailStats$(id: number): Observable<AnnouncementEmailStats> {
    return this.#httpClient.get<AnnouncementEmailStats>(
      `${this.#apiUrl}/${id}/email-stats`,
      { withCredentials: true },
    );
  }
}
